(() => {
  if (window.StellarSectionJump) return;
  const root = document.documentElement;
  const reserved = new Set(['recover', 'login', 'hero-wishlist', 'products', 'article', 'page', 'spark-account']);
  let landing = null, frame = 0, observer = null, reviewWait = 0;
  const cancel = () => {
    landing = null;
    clearTimeout(reviewWait);
    cancelAnimationFrame(frame);
    observer?.disconnect();
    observer = null;
  };
  const headerOffset = () => {
    const header = document.querySelector('.shopify-section-header');
    const height = header?.getBoundingClientRect().height || 0;
    // Reserve room for the sticky header even when its scroll-down animation hides it.
    const offset = Math.ceil(Math.min(height, innerHeight * .45)) + 16;
    root.style.setProperty('--stellar-jump-header-offset', offset + 'px');
    return offset;
  };
  const position = () => {
    const target = landing;
    if (!target?.isConnected || !target.getClientRects().length) return;
    const offset = headerOffset();
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
    const maximum = Math.max(0, document.documentElement.scrollHeight - innerHeight);
    const destination = Math.min(top, maximum);
    if (Math.abs(destination - window.scrollY) > 1) window.scrollTo({ top: destination, behavior: 'instant' });
  };
  const schedule = () => {
    if (!landing) return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(position);
  };
  const resolve = hash => {
    if (!hash || hash === '#' || hash === '##' || hash.includes(':~:text=')) return null;
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (_) { return null; }
    if (reserved.has(id)) return null;
    const target = document.getElementById(id);
    if (!target || target.matches('input,select,textarea,button,svg,symbol') || target.closest('dialog,modal-dialog,quick-add-modal,product-modal,[role="tabpanel"],.menu-drawer,.swiper')) return null;
    if (target.closest('[hidden],[aria-hidden="true"]')) return null;
    if (!target.getClientRects().length && !target.closest('details:not([open])')) return null;
    return target;
  };
  const go = (target, { focus = true } = {}) => {
    if (!target?.isConnected) return false;
    cancel();
    for (let node = target; node; node = node.parentElement) {
      if (node instanceof HTMLDetailsElement) node.open = true;
    }
    if (!target.getClientRects().length) return false;
    landing = target;
    target.setAttribute('data-stellar-jump-target', '');
    if (focus) {
      const heading = target.matches('h1,h2,h3,h4,h5,h6,main,summary') ? target : target.querySelector('h1,h2,h3,h4,h5,h6,summary') || target;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    position();
    schedule();
    // Observe real layout changes while landing; any user scroll/interaction cancels this.
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(schedule);
      const header = document.querySelector('.shopify-section-header');
      if (header) observer.observe(header);
      for (const section of document.querySelectorAll('main > *, .shopify-section-group-header-group')) {
        if (section.contains(target) || (section.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)) observer.observe(section);
      }
    }
    document.fonts?.ready.then(schedule);
    return true;
  };
  window.StellarSectionJump = { to: go, cancel };
  const sameDocument = url => {
    const current = new URL(location.href);
    current.searchParams.delete('preview_theme_id');
    const incoming = new URL(url.href);
    incoming.searchParams.delete('preview_theme_id');
    return incoming.origin === current.origin && incoming.pathname.replace(/\/$/, '') === current.pathname.replace(/\/$/, '') && incoming.search === current.search;
  };
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    if (link.matches('[role="tab"],[aria-haspopup],[data-width-compare-open],[data-support-trigger],.tab-btn') || link.closest('dialog,modal-opener,modal-dialog,quick-add-modal,product-modal,.menu-drawer')) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (!sameDocument(url)) return;
    const target = resolve(url.hash);
    if (!target) return;
    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(history.state, '', url.hash);
    // Keyboard and assistive-technology activation should move focus to the
    // destination. Pointer navigation only needs to position the section.
    go(target, { focus: event.detail === 0 });
  }, true);
  const followHash = () => {
    cancel();
    const hash = location.hash;
    const deadline = Date.now() + 8000;
    const attempt = () => {
      if (location.hash !== hash) return;
      const target = resolve(hash);
      // Fragment navigation scrolls without stealing keyboard focus.
      if (target) go(target, { focus: false });
      else if (hash === '#judgeme_product_reviews' && Date.now() < deadline) {
        reviewWait = setTimeout(attempt, 250);
      }
    };
    attempt();
  };
  window.addEventListener('hashchange', followHash);
  window.addEventListener('load', schedule);
  window.addEventListener('resize', () => { headerOffset(); schedule(); }, { passive: true });
  document.addEventListener('load', event => {
    if (landing && event.target instanceof HTMLImageElement && (event.target.compareDocumentPosition(landing) & Node.DOCUMENT_POSITION_FOLLOWING)) schedule();
  }, true);
  for (const type of ['wheel', 'touchstart', 'pointerdown']) window.addEventListener(type, cancel, { passive: true });
  window.addEventListener('keydown', event => {
    if (['ArrowUp','ArrowDown','PageUp','PageDown','Home','End','Tab',' '].includes(event.key)) cancel();
  });
  window.addEventListener('pagehide', cancel);
  document.addEventListener('shopify:section:unload', cancel);
  document.addEventListener('shopify:section:load', () => { headerOffset(); if (landing) schedule(); });
  headerOffset();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', followHash, { once: true });
  else followHash();
})();

/* Preserve explicit draft previews on internal discovery links, including rerenders. */
(() => {
  if (window.StellarDraftLinks) return;
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  if (!preview || !/^\d+$/.test(preview)) return;
  const patchLink = link => {
    if (!link?.matches?.('a[href]') || link.hasAttribute('download')) return;
    const raw = link.getAttribute('href');
    if (!raw || raw.startsWith('#')) return;
    let url;
    try { url = new URL(raw, location.href); } catch (_) { return; }
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;
    const path = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '');
    if (path !== '' && path !== '/' && !/^\/(collections|products|blogs|pages)(\/|$)/.test(path)) return;
    if (url.searchParams.has('preview_theme_id')) return;
    url.searchParams.set('preview_theme_id', preview);
    link.setAttribute('href', url.pathname + url.search + url.hash);
  };
  const patch = node => {
    patchLink(node);
    node.querySelectorAll?.('a[href]').forEach(patchLink);
  };
  window.StellarDraftLinks = { patch };
  patch(document);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') patchLink(record.target);
      else record.addedNodes.forEach(patch);
    }
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  document.addEventListener('pointerdown', event => patchLink(event.target.closest?.('a[href]')), true);
})();
